import { dispatch, ACTION_TYPES } from '../actions/index';
import {
  decideAllocationMode, allocateDice, eligibleChargeTargets, sectorHasColony, meetsClaimCondition, resolveBonusRollChoice,
  resolveNeighborChoice,
} from '../actions/turnActions';
import { isAbilityCharged } from '../actions/abilities';
import { defFor } from '../cards/cardHelpers';
import { SECTORS } from '../constants';

// Matt: "figure out how we can add an AI player into this so I can play
// against the computer... I should be able to play against as many
// computers as I want to." Mirrors the thunderstone repo's own
// src/engine/ai/heuristicBot.js at the architectural level: the AI is
// just another player dispatching through the exact same
// dispatch(state, action) API a human's UI uses - nothing here has
// special engine-level privileges. The turn shape here is much flatter
// than Thunderstoner's (no village/dungeon/rest/prepare branching) - roll,
// allocate, buy-or-skip, end turn - so one playAITurn() call does the
// whole turn in a loop, the same way Thunderstoner's does.

const VALUE_WEIGHTS = { credits: 1, income: 2.5, vp: 4 };

function scorePlayer(state, playerId) {
  const p = state.players.find((pl) => pl.id === playerId);
  if (!p) return 0;
  return p.credits * VALUE_WEIGHTS.credits + p.income * VALUE_WEIGHTS.income + p.vp * VALUE_WEIGHTS.vp;
}

// The active roller ALWAYS has a real choice to make (decideAllocationMode
// returns null for them - see its own comment: every sector always has a
// station, so both sum and separate always hit something). allocateDice
// is a pure function, so simulating both outcomes off the SAME original
// state and comparing the resulting score is safe - no need to hand-model
// every reward type's value separately.
export function chooseAllocationMode(state, playerId) {
  const before = scorePlayer(state, playerId);
  const afterSum = allocateDice(state, playerId, 'sum');
  const afterSeparate = allocateDice(state, playerId, 'separate');
  const sumGain = scorePlayer(afterSum, playerId) - before;
  const separateGain = scorePlayer(afterSeparate, playerId) - before;
  return separateGain > sumGain ? 'separate' : 'sum';
}

// Resolves ANY player's (active or passive, human or AI) pending
// allocation using decideAllocationMode first (the shared "is this even
// a real choice" rule - see its own comment) and only falling back to the
// scored heuristic above when it genuinely is one. Store/index.js calls
// this for every AI player currently up to allocate, regardless of
// whether they're this turn's active roller.
export function resolveAllocationForAI(state, playerId) {
  const mode = decideAllocationMode(state, playerId) || chooseAllocationMode(state, playerId);
  return dispatch(state, { type: ACTION_TYPES.ALLOCATE_DICE, payload: { playerId, mode } });
}

// Matt reported this directly: bonus rolls (turnActions.js's own
// enqueueBonusRoll/resolveBonusRollChoice) now need a REAL sum/separate
// choice instead of auto-resolving - the front pendingBonusRolls entry
// (only one is ever "live" at a time) is never unambiguous for its own
// owner (same "always as the active player" reasoning as the main roll -
// decideAllocationMode's own comment), so this always falls to the scored
// heuristic below in practice, same shape as resolveAllocationForAI/
// chooseAllocationMode above just against resolveBonusRollChoice instead
// of allocateDice (a bonus roll's own private dice values, not
// state.meta.dice).
export function chooseBonusRollMode(state, playerId) {
  const before = scorePlayer(state, playerId);
  const afterSum = resolveBonusRollChoice(state, { playerId, mode: 'sum' });
  const afterSeparate = resolveBonusRollChoice(state, { playerId, mode: 'separate' });
  const sumGain = scorePlayer(afterSum, playerId) - before;
  const separateGain = scorePlayer(afterSeparate, playerId) - before;
  return separateGain > sumGain ? 'separate' : 'sum';
}

export function resolveBonusRollForAI(state, playerId) {
  const entry = state.meta.pendingBonusRolls?.[0];
  if (!entry || entry.playerId !== playerId) return state;
  const mode = decideAllocationMode(state, playerId, entry.values, true) || chooseBonusRollMode(state, playerId);
  return dispatch(state, { type: ACTION_TYPES.RESOLVE_BONUS_ROLL, payload: { playerId, mode } });
}

// Genesis's The Flying Dutchman - the AI's own answer to a real
// pendingNeighborChoices entry (3+ players; a 2-player game auto-resolves
// engine-side, never reaching here). Same "simulate both real outcomes
// off the same base state, compare score" shape as chooseAllocationMode/
// chooseBonusRollMode above - resolveNeighborChoice is a pure function, so
// calling it twice (once per candidate neighbor) from the SAME state and
// comparing is safe.
export function chooseNeighborChoiceTarget(state, playerId, choiceIndex, entry) {
  const before = scorePlayer(state, playerId);
  const afterLeft = resolveNeighborChoice(state, { playerId, choiceIndex, chosenPlayerId: entry.leftId });
  const afterRight = resolveNeighborChoice(state, { playerId, choiceIndex, chosenPlayerId: entry.rightId });
  const leftGain = scorePlayer(afterLeft, playerId) - before;
  const rightGain = scorePlayer(afterRight, playerId) - before;
  return rightGain > leftGain ? entry.rightId : entry.leftId;
}

export function resolveNeighborChoiceForAI(state, playerId) {
  const choiceIndex = (state.meta.pendingNeighborChoices || []).findIndex((c) => c.playerId === playerId);
  if (choiceIndex === -1) return state;
  const entry = state.meta.pendingNeighborChoices[choiceIndex];
  const chosenPlayerId = chooseNeighborChoiceTarget(state, playerId, choiceIndex, entry);
  return dispatch(state, { type: ACTION_TYPES.RESOLVE_NEIGHBOR_CHOICE, payload: { playerId, choiceIndex, chosenPlayerId } });
}

function slotValue(slot) {
  if (!slot) return 0;
  if (slot.ability) return 6; // chargeable abilities are generally strong - flat heuristic, hard to price exactly.
  if (slot.reward) {
    const { kind, amount } = slot.reward;
    if (kind === 'text') return 5; // claim/buy-card effects - decent flat value, same reasoning as abilities above.
    return (amount || 0) * (VALUE_WEIGHTS[kind] || 1);
  }
  return 0;
}

// A rough "how good is owning this card" score: its own station value
// (triggers on THIS player's rolls) plus a discounted deployed value (only
// triggers on OTHER players' rolls, so worth less to the buyer), minus a
// mild cost-efficiency penalty so a same-value cheaper card wins ties.
function cardValue(def) {
  return slotValue(def.station) + slotValue(def.deployed) * 0.4 - (def.cost || 0) * 0.5;
}

// Genesis's own buy-time "OR" colonies (Carman/Tzioti/Wood/Buckendorf/
// Manolis/Eaton/McCool/Livingston/Zimmerman) - a rough estimate of
// `def.orChoice`'s own value, in the same VALUE_WEIGHTS units as
// `def.vp * VALUE_WEIGHTS.vp`, so the AI can pick whichever is bigger.
// Not exact (a sectorRangeReward's own "gain your printed rewards for
// sectors X-Y" depends on exactly what's already on this player's board,
// approximated here via the same slotValue already used for cardValue
// above), but good enough to not always leave the alternate on the table.
function estimateOrChoiceValue(state, playerId, orChoice) {
  if (orChoice.kind === 'flatResource') {
    return (orChoice.reward.amount || 0) * (VALUE_WEIGHTS[orChoice.reward.kind] || 1);
  }
  if (orChoice.kind === 'sectorRangeReward') {
    const player = state.players.find((p) => p.id === playerId);
    const zones = orChoice.zoneChoice ? ['station', 'deployed'] : (orChoice.zone === 'both' ? ['station', 'deployed'] : [orChoice.zone]);
    let total = 0;
    for (let s = orChoice.min; s <= orChoice.max; s += 1) {
      const sector = player.sectors[s];
      if (!sector) continue;
      if (zones.includes('station') && sector.station) {
        total += slotValue(defFor(sector.station.cardId).station);
      }
      if (zones.includes('deployed')) {
        sector.deployedStack.forEach((inst) => { total += slotValue(defFor(inst.cardId).deployed); });
      }
    }
    // zoneChoice (Livingston's own "red OR blue") only ever grants ONE of
    // the two zones, not both - halving the combined estimate is a rough
    // stand-in for "pick whichever zone turns out bigger" without
    // actually computing both and comparing (station/deployed are
    // summed together above for simplicity).
    return orChoice.zoneChoice ? total / 2 : total;
  }
  return 0;
}

// Returns { colonyChoice: 'vp'|'or', colonyOrZone } for a colony with an
// `orChoice` - 'vp' (the default, matching "you don't have to take the
// alternate") unless the alternate's own estimated value is strictly
// bigger. `colonyOrZone` only matters for Livingston's own genuine
// zone-choice shape; picking 'deployed' arbitrarily there since the
// estimate above already blends both zones into one number.
function colonyChoiceFor(state, playerId, def) {
  // Satkowiak's trade target: the VP leader among opponents, same
  // "give the leader a chance to hand back a dud" instinct as the
  // Fitzgerald gift target below.
  if (def.tradeCards) {
    const opponents = state.players.filter((p) => p.id !== playerId);
    if (opponents.length) {
      const target = opponents.reduce((best, p) => (p.vp > best.vp ? p : best), opponents[0]);
      return { colonyTargetPlayerId: target.id };
    }
    return {};
  }
  if (!def.orChoice) return {};
  const vpValue = (def.vp || 0) * VALUE_WEIGHTS.vp;
  const orValue = estimateOrChoiceValue(state, playerId, def.orChoice);
  if (orValue <= vpValue) return { colonyChoice: 'vp' };
  return { colonyChoice: 'or', colonyOrZone: def.orChoice.zoneChoice ? 'deployed' : undefined };
}

// Every affordable option a buy/claim/extra-buy could spend on right now,
// best first. `matches` narrows further (claims are locked to one printed
// level; a free claim/extra-buy ignores cost entirely).
function buyableOptions(state, playerId, {
  free = false, level = null, colonyOnly = false, colonyHalfCost = false, colonyDiscount = 0, isClaim = false,
} = {}) {
  const player = state.players.find((p) => p.id === playerId);
  const options = [];
  if (!colonyOnly) {
    [1, 2, 3].forEach((lvl) => {
      if (level != null && lvl !== level) return;
      state.shipyard.display[lvl].forEach((cardId) => {
        const def = defFor(cardId);
        // A colony already sitting on this ship's own target sector (its
        // printed minimum, for a variableSector card - the same sector
        // this AI always ends up placing at, per sectorOverrideFor's own
        // "simplest valid answer" convention) makes a normal BUY illegal
        // (buyCard's own sectorHasColony guard) - found via
        // selfPlaySmoke.test.js: without this filter, the AI's own
        // top-ranked "option" could be genuinely unbuyable with no
        // fallback to the next one, permanently stalling that player's
        // own turn (playAITurn's post-reward branch only ever tries
        // options[0], never retries on failure). A CLAIM is unaffected -
        // claimCard already deploys gracefully into a colonized sector
        // instead of rejecting outright (its own sectorHasColony check
        // branches to deployCardIntoColonizedSector, never a no-op).
        if (!isClaim && sectorHasColony(player, def.sector)) return;
        if (free || def.cost <= player.credits) options.push({ level: lvl, cardId, value: cardValue(def) });
      });
    });
  }
  if (!level) {
    state.colonies.forEach((c) => {
      if (c.taken) return;
      const def = defFor(c.cardId);
      // Genesis's Haumea (see turnActions.js's own buyColonyHalfCost
      // comment) - the printed cost rounds up/2 for this one extra buy.
      // Terra Proxima's genesis-ship uses a flat amount off instead.
      let cost = def.cost;
      if (colonyHalfCost) cost = Math.ceil(def.cost / 2);
      else if (colonyDiscount) cost = Math.max(0, def.cost - colonyDiscount);
      // Genesis's "Claimed" colonies (Akey/Harbison/Mara/Bongiu) print
      // cost 0, so they'd otherwise always look "affordable" here even
      // when their own board-state condition isn't met yet - buyCard
      // itself already rejects that (returns state unchanged), but
      // without this check the AI would keep re-picking the same
      // now-illegal top-ranked option every turn forever, a real stall
      // found via selfPlaySmoke.test.js the moment these existed at all.
      if (def.claimCondition && !meetsClaimCondition(player, def.claimCondition)) return;
      // A colony already holding this colony's own destination sector
      // (possible once Holcomb/Circarth can place colonies off their
      // printed sector) makes the buy illegal - skip it, same stall
      // reasoning as the ship filter above.
      if (sectorHasColony(player, def.sector)) return;
      if (free || cost <= player.credits) options.push({ colonyCardId: c.cardId, value: cardValue(def) });
    });
  }
  options.sort((a, b) => b.value - a.value);
  return options;
}

// A variable-sector card (Biodome's Nomad Class, etc.) needs an explicit
// sector - the printed minimum is always a legal choice, so that's what
// the AI uses rather than reasoning about which sector is "best" (a human
// player gets the same picker; this just always takes the simplest valid
// answer, same convention selfPlaySmoke.test.js's own bot loop already
// used before this real AI existed).
function sectorOverrideFor(def, rangeMin) {
  if (!def.variableSector) return undefined;
  return Math.max(def.sector, rangeMin || def.sector);
}

// Resolves every pending claim/extra-buy owed to this player (active-
// player-only mechanics - see claimCard/buyCard's own playerId checks) by
// spending each on its best affordable (or, for a claim/free extra-buy,
// best available regardless of cost) option. Loops because resolving one
// can occasionally reveal further state to react to next pass, and because
// pendingExtraBuys' own array indices shift as each entry is consumed.
// Exported (not just used internally by playAITurn) because a claim/extra-
// buy can belong to a PASSIVE player - store/index.js's driveAITurnsStep
// calls this directly for whichever AI player currently owns one,
// independent of whose turn it nominally is (see turnActions.js's own
// comments on claimCard/buyCard for why that's possible - e.g.
// l2-kovalyonok's deployed reward, or a red-timed buyCardSectorRange
// ability).
export function resolvePendingSpends(state, playerId) {
  let next = state;
  let guard = 0;
  for (;;) {
    guard += 1;
    if (guard > 20) break; // generous but bounded - see tryUseReadyAbilities's own comment for why this pattern recurs.
    // Matt reported this directly: "my starting card... a card that
    // could've gone in any slot one or higher, but I wasn't given a
    // choice of where to place it." An AI's own variable-sector setup
    // draw (createGameState.js's own comment on drawSetupCard) needs
    // resolving the same way a human's does - always the printed
    // minimum, same "always take the simplest valid answer" convention
    // sectorOverrideFor already uses for every other AI variable-sector
    // pick.
    const setupPlacement = (next.meta.pendingSetupPlacements || []).find((p) => p.playerId === playerId);
    if (setupPlacement) {
      next = dispatch(next, {
        type: ACTION_TYPES.PLACE_SETUP_CARD,
        payload: { playerId, sectorOverride: setupPlacement.minSector },
      });
      continue;
    }
    // A "choose the sector to your left or right" pick (turnActions.js's
    // own resolveArrowChoice) - same slotValue heuristic buyableOptions
    // already uses to rank cards, summed across everything that branch
    // would actually grant (every hop's own ownBaseRewards - can be more
    // than one now that secondaryReward/multi-card deployed stacks are
    // folded in there too - plus its own leafReward, if any) rather than
    // just a single reward field. Resolved before claims/extra-buys below
    // since it's the same post-reward-phase pending state and this loop
    // already re-scans from scratch each pass regardless.
    const optionValue = (option) => option.ownBaseRewards.reduce((sum, r) => sum + slotValue({ reward: r }), 0)
      + slotValue({ reward: option.leafReward });
    const arrowChoice = (next.meta.pendingArrowChoices || []).find((c) => c.playerId === playerId);
    if (arrowChoice) {
      const choiceIndex = next.meta.pendingArrowChoices.indexOf(arrowChoice);
      const best = arrowChoice.options.reduce((a, b) => (optionValue(b) > optionValue(a) ? b : a));
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_ARROW_CHOICE,
        payload: { playerId, choiceIndex, chosenSector: best.sector },
      });
      continue;
    }
    // Shy Pluto's "gain rewards for any N sectors 1-6" pick (turnActions.js's
    // own resolveSectorPick) - picks the `count` highest-value legal
    // sectors by their own `pick.zone` slot value, same slotValue
    // heuristic as everything else here. Every sector in range always has
    // SOME station card after setup (createGameState's own setup loop -
    // same fact resolveBonusRoll leans on), so the 'station' case (the
    // only variant the rulebook describes) always has real candidates to
    // rank; a hypothetical future 'deployed' variant falls back to
    // whichever deployed cards happen to be there, zero-valued sectors
    // included, rather than special-casing something no real card uses.
    const sectorPick = (next.meta.pendingSectorPicks || []).find((p) => p.playerId === playerId);
    if (sectorPick) {
      const pickIndex = next.meta.pendingSectorPicks.indexOf(sectorPick);
      const player = next.players.find((p) => p.id === playerId);
      const candidates = [];
      for (let s = sectorPick.min; s <= sectorPick.max; s += 1) {
        if ((sectorPick.excludeSectors || []).includes(s)) continue; // turnActions.js's own pickSectors cycle guard
        const sector = player.sectors[s];
        if (!sector) continue;
        const slot = sectorPick.zone === 'station'
          ? (sector.station ? defFor(sector.station.cardId).station : null)
          : (sector.deployedStack[0] ? defFor(sector.deployedStack[0].cardId).deployed : null);
        candidates.push({ sector: s, value: slotValue(slot) });
      }
      if (candidates.length < sectorPick.count) {
        // turnActions.js's own excludeSectors cycle guard (grantReward's
        // pickSectors case) can, after a long enough chain of
        // pickSectors-into-pickSectors resolutions, exclude enough
        // sectors that fewer than `count` legal candidates remain at
        // all - decline rather than leave it pending forever (same
        // reasoning as a sold-out claim/unaffordable extra buy already
        // declining above).
        next = dispatch(next, { type: ACTION_TYPES.DECLINE_SECTOR_PICK, payload: { playerId, pickIndex } });
        continue;
      }
      candidates.sort((a, b) => b.value - a.value);
      const chosenSectors = candidates.slice(0, sectorPick.count).map((c) => c.sector);
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_SECTOR_PICK,
        payload: { playerId, pickIndex, chosenSectors },
      });
      continue;
    }
    // Genesis's Dalton pick (turnActions.js's own resolveDeckPeek) - a
    // free claim-or-decline per revealed card, so the AI just claims
    // anything with positive value (same cardValue heuristic
    // buyableOptions already uses to rank purchases) and declines the
    // rest; a variableSector card always takes its own printed minimum
    // sector, same "simplest valid answer" convention every other
    // variable-sector AI pick in this file already uses.
    const deckPeek = (next.meta.pendingDeckPeeks || []).find((p) => p.playerId === playerId);
    if (deckPeek) {
      const peekIndex = next.meta.pendingDeckPeeks.indexOf(deckPeek);
      const decisions = deckPeek.cardIds.map((cardId) => {
        const def = defFor(cardId);
        const worthClaiming = cardValue(def) > 0;
        return {
          cardId, claim: worthClaiming, sectorOverride: worthClaiming ? sectorOverrideFor(def, def.sector) : undefined,
        };
      });
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_DECK_PEEK,
        payload: { playerId, peekIndex, decisions },
      });
      continue;
    }
    // Genesis's Helios/Radiant/Watts (deployed) pick (turnActions.js's
    // own resolveChargeChoice) - "up to N", so the AI just charges as
    // many eligible cards as it can (any charge is generically good,
    // same reasoning `slot.ability` already scores flat in slotValue) up
    // to the printed max, simplest-first (order doesn't matter - there's
    // no per-target cost/value differentiation worth ranking here).
    const chargeChoice = (next.meta.pendingChargeChoices || []).find((c) => c.playerId === playerId);
    if (chargeChoice) {
      const choiceIndex = next.meta.pendingChargeChoices.indexOf(chargeChoice);
      const targets = eligibleChargeTargets(next, playerId, chargeChoice.maxSector, chargeChoice.boardOwnerId || playerId).slice(0, chargeChoice.max);
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_CHARGE_CHOICE,
        payload: { playerId, choiceIndex, targets },
      });
      continue;
    }
    // Genesis's Triumph/Ulric pick (turnActions.js's own resolveMoveChoice)
    // - grantReward's own moveDeployedCard case already skips queuing this
    // at all when there's nothing deployed to move, so reaching here
    // means at least one legal (instance, destination) pair exists;
    // simplest-first (the first deployed card found, the first printed
    // destination) - no strategic signal strong enough to rank "which
    // card to relocate" better than any other convention already used in
    // this file for an equally arbitrary pick.
    const moveChoice = (next.meta.pendingMoveChoices || []).find((c) => c.playerId === playerId);
    if (moveChoice) {
      const choiceIndex = next.meta.pendingMoveChoices.indexOf(moveChoice);
      const player = next.players.find((p) => p.id === playerId);
      let instanceId = null;
      let sourceSector = null;
      SECTORS.forEach((s) => {
        if (instanceId || !player.sectors[s]) return;
        const found = player.sectors[s].deployedStack[0];
        if (found) { instanceId = found.instanceId; sourceSector = s; }
      });
      // Ulric's own destinations are computed live (adjacent to whichever
      // card got picked above), not a fixed printed list like Triumph's -
      // see turnActions.js's own resolveMoveChoice comment.
      const destinationSector = moveChoice.adjacentOnly
        ? [sourceSector - 1, sourceSector + 1].find((s) => s >= 1 && s <= 12)
        : moveChoice.destinations[0];
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_MOVE_CHOICE,
        payload: {
          playerId, choiceIndex, instanceId, destinationSector,
        },
      });
      continue;
    }
    // Genesis's Maddock pick (turnActions.js's own resolveCardSwapChoice)
    // - always legal via zone: 'station' (every sector always has a
    // station), so simplest-first is just sectors 1/2, same "no strategic
    // signal strong enough to rank this better" reasoning as the move
    // choice just above.
    const cardSwapChoice = (next.meta.pendingCardSwapChoices || []).find((c) => c.playerId === playerId);
    if (cardSwapChoice) {
      const choiceIndex = next.meta.pendingCardSwapChoices.indexOf(cardSwapChoice);
      // Valiant/Leslie's own anchored variant (turnActions.js's own
      // resolveCardSwapChoice) - one side MUST be anchorSector, so pick
      // whichever neighbor is actually in-bounds (12+1 doesn't exist,
      // 1-1 doesn't exist) instead of the unrestricted "just use 1/2"
      // Maddock's own case defaults to below.
      const { anchorSector } = cardSwapChoice;
      const sectorA = anchorSector ?? 1;
      const sectorB = anchorSector != null ? (anchorSector < 12 ? anchorSector + 1 : anchorSector - 1) : 2;
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_CARD_SWAP,
        payload: {
          playerId, choiceIndex, sectorA, sectorB, zone: 'station',
        },
      });
      continue;
    }
    // Genesis's Pharos pick (turnActions.js's own resolveEmptySectorChoice)
    // - grantReward's own emptySectorChoice case already skips queuing
    // this at all when there's no occupied sector to empty, so reaching
    // here means at least one legal sector exists; simplest-first (the
    // first occupied sector found), same reasoning as the move/swap
    // choices above.
    const emptySectorChoice = (next.meta.pendingEmptySectorChoices || []).find((c) => c.playerId === playerId);
    if (emptySectorChoice) {
      const choiceIndex = next.meta.pendingEmptySectorChoices.indexOf(emptySectorChoice);
      const player = next.players.find((p) => p.id === playerId);
      const sector = SECTORS.find((s) => player.sectors[s]?.station);
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_EMPTY_SECTOR_CHOICE,
        payload: { playerId, choiceIndex, sector },
      });
      continue;
    }
    // Biodome's Planton pick (resolveShipDiscard) - discard the cheapest
    // ship on the board (deployed preferred over station: a deployed card
    // has already done its bumping work), then the claim happens inside
    // the resolver.
    const shipDiscard = (next.meta.pendingShipDiscards || []).find((c) => c.playerId === playerId);
    if (shipDiscard) {
      const choiceIndex = next.meta.pendingShipDiscards.indexOf(shipDiscard);
      const player = next.players.find((p) => p.id === playerId);
      const colonyIds = new Set(next.colonies.map((c) => c.cardId));
      const candidates = [];
      Object.values(player.sectors).forEach((s) => {
        s.deployedStack.forEach((d) => { if (!colonyIds.has(d.cardId)) candidates.push({ inst: d, cost: defFor(d.cardId).cost, deployed: true }); });
        if (s.station && !colonyIds.has(s.station.cardId)) candidates.push({ inst: s.station, cost: defFor(s.station.cardId).cost, deployed: false });
      });
      candidates.sort((a, b) => (Number(b.deployed) - Number(a.deployed)) || (a.cost - b.cost));
      if (candidates.length) {
        next = dispatch(next, {
          type: ACTION_TYPES.RESOLVE_SHIP_DISCARD,
          payload: { playerId, choiceIndex, instanceId: candidates[0].inst.instanceId },
        });
        continue;
      }
      // A board of nothing but colonies has no legal discard - impossible
      // in practice, but never leave a pending choice wedged.
      next = { ...next, meta: { ...next.meta, pendingShipDiscards: next.meta.pendingShipDiscards.filter((_, i) => i !== choiceIndex) } };
      continue;
    }
    // Biodome's Thrivax pick (resolveDieRewardChoice) - take the extra
    // die reward for the higher-numbered sector; free rewards are never
    // worth declining.
    const dieRewardChoice = (next.meta.pendingDieRewardChoices || []).find((c) => c.playerId === playerId);
    if (dieRewardChoice) {
      const choiceIndex = next.meta.pendingDieRewardChoices.indexOf(dieRewardChoice);
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_DIE_REWARD_CHOICE,
        payload: { playerId, choiceIndex, sector: Math.max(...dieRewardChoice.options) },
      });
      continue;
    }
    // Genesis's Satkowiak give (resolveCardTradeChoice) - hand over the
    // cheapest card on the board, same "give away the least" instinct a
    // human defaults to.
    const cardTradeChoice = (next.meta.pendingCardTradeChoices || []).find((c) => c.playerId === playerId);
    if (cardTradeChoice) {
      const choiceIndex = next.meta.pendingCardTradeChoices.indexOf(cardTradeChoice);
      const player = next.players.find((p) => p.id === playerId);
      const colonyIds = new Set(next.colonies.map((c) => c.cardId));
      const candidates = [];
      Object.values(player.sectors).forEach((s) => {
        s.deployedStack.forEach((d) => { if (!colonyIds.has(d.cardId)) candidates.push({ inst: d, cost: defFor(d.cardId).cost }); });
        if (s.station && !colonyIds.has(s.station.cardId)) candidates.push({ inst: s.station, cost: defFor(s.station.cardId).cost });
      });
      candidates.sort((a, b) => a.cost - b.cost);
      if (candidates.length) {
        next = dispatch(next, {
          type: ACTION_TYPES.RESOLVE_CARD_TRADE,
          payload: { playerId, choiceIndex, instanceId: candidates[0].inst.instanceId },
        });
        continue;
      }
      next = { ...next, meta: { ...next.meta, pendingCardTradeChoices: next.meta.pendingCardTradeChoices.filter((_, i) => i !== choiceIndex) } };
      continue;
    }
    // Genesis's Fitzgerald pick (turnActions.js's own resolveCardGiftTarget)
    // - "choose an opponent" to hand a drawn card to, who then decides
    // claim-or-discard themselves (a separate pendingDeckPeeks entry this
    // same function already resolves generically above). No printed
    // guidance on WHO to target, so (documented in
    // docs/expansion-decisions-needed.md) the AI targets whoever currently
    // has the most vp - "give the leader a card that might be a dud" is a
    // defensible simplification of a real player's own judgment call here.
    const cardGiftChoice = (next.meta.pendingCardGiftChoices || []).find((c) => c.playerId === playerId);
    if (cardGiftChoice) {
      const choiceIndex = next.meta.pendingCardGiftChoices.indexOf(cardGiftChoice);
      const opponents = next.players.filter((p) => p.id !== playerId);
      const targetPlayerId = opponents.reduce((best, p) => (p.vp > best.vp ? p : best), opponents[0]).id;
      next = dispatch(next, {
        type: ACTION_TYPES.RESOLVE_CARD_GIFT_TARGET,
        payload: { playerId, choiceIndex, targetPlayerId },
      });
      continue;
    }
    const claim = (next.meta.pendingClaims || []).find((c) => c.playerId === playerId);
    if (claim) {
      const claimIndex = next.meta.pendingClaims.indexOf(claim);
      const options = buyableOptions(next, playerId, { free: true, level: claim.level, isClaim: true });
      if (!options.length) {
        // That level's shipyard deck is fully sold out - nothing will ever
        // change that mid-game, so decline rather than leave it pending
        // forever (which would permanently block this player's own
        // endTurn - see turnActions.js's own comment on declineClaim).
        next = dispatch(next, { type: ACTION_TYPES.DECLINE_CLAIM, payload: { playerId, claimIndex } });
        continue;
      }
      const choice = options[0];
      const def = defFor(choice.cardId);
      next = dispatch(next, {
        type: ACTION_TYPES.CLAIM_CARD,
        payload: {
          playerId, level: claim.level, cardId: choice.cardId, claimIndex, sectorOverride: sectorOverrideFor(def, def.sector),
        },
      });
      continue;
    }
    const extraBuy = (next.meta.pendingExtraBuys || []).find((eb) => eb.playerId === playerId);
    if (extraBuy) {
      const extraBuyIndex = next.meta.pendingExtraBuys.indexOf(extraBuy);
      const options = buyableOptions(next, playerId, {
        free: extraBuy.free, colonyOnly: extraBuy.colonyOnly, colonyHalfCost: extraBuy.colonyHalfCost, colonyDiscount: extraBuy.colonyDiscount,
      });
      if (!options.length) {
        // Too poor to afford anything on offer - same reasoning as the
        // claim branch above (decline rather than block this player's own
        // endTurn forever).
        next = dispatch(next, { type: ACTION_TYPES.DECLINE_EXTRA_BUY, payload: { playerId, extraBuyIndex } });
        continue;
      }
      const choice = options[0];
      const def = defFor(choice.colonyCardId || choice.cardId);
      const rangeMin = extraBuy.sectorRange ? extraBuy.sectorRange[0] : undefined;
      // buyCard requires an explicit sectorOverride whenever the extra buy
      // carries a sectorRange, for ANY card - not just a variable-sector
      // one (deployCard always honors sectorOverride when given, and a
      // buyCardSectorRange ability's whole point is placement flexibility
      // for whatever's bought). sectorOverrideFor only ever returns
      // non-undefined for a variable-sector card, so a normal card needs
      // this fallback to rangeMin or buyCard rejects the dispatch outright
      // - same fallback GameBoard.vue's own useExtraBuy already uses for a
      // human player (`sectorOverride ?? (eb.sectorRange ? eb.sectorRange[0] : undefined)`).
      const sectorOverride = choice.colonyCardId
        ? undefined
        : (sectorOverrideFor(def, rangeMin) ?? rangeMin);
      next = dispatch(next, {
        type: ACTION_TYPES.BUY_CARD,
        payload: {
          playerId,
          level: choice.level,
          cardId: choice.cardId,
          colonyCardId: choice.colonyCardId,
          extraBuyIndex,
          sectorOverride,
          ...(choice.colonyCardId ? colonyChoiceFor(next, playerId, def) : {}),
        },
      });
      continue;
    }
    break;
  }
  return next;
}

// Fires every currently-charged ability this player owns (station or
// deployed) opportunistically - the engine's own useAbility already gates
// legality (timing window, charge level, phase), so this doesn't need to
// hand-model exactly when each ability is usable; it just tries anything
// ready and lets a no-op dispatch harmlessly do nothing if the moment
// isn't actually legal. Re-scans from scratch after each successful use
// (guarded, bounded) since a swap-sectors-style ability can reshuffle
// which card sits where mid-scan. Exported: a PASSIVE AI player's own
// red/green-timed charged ability never gets an active-player's-turn loop
// to piggyback on (playAITurn only ever drives the active player), so
// store/index.js's driveAITurnsStep calls this directly for passive AI
// players too - mirroring how a human resolves the same thing today, by
// switching to their own board's tab and clicking the ability whenever
// it's charged (see GameBoard.vue's onUseAbility, which already lets the
// VIEWED player use their own ability regardless of whose turn it is).
export function tryUseReadyAbilities(state, playerId) {
  let next = state;
  let guard = 0;
  for (;;) {
    guard += 1;
    if (guard > 30) break;
    const player = next.players.find((p) => p.id === playerId);
    if (!player) break;
    let dispatched = false;
    for (const sectorNum of SECTORS) {
      const sector = player.sectors[sectorNum];
      if (!sector) continue;
      if (sector.station) {
        const def = defFor(sector.station.cardId);
        if (def.station?.ability && isAbilityCharged(sector.station, 'station', def.station.ability, next.players.length)) {
          const after = dispatch(next, {
            type: ACTION_TYPES.USE_ABILITY,
            payload: { playerId, instanceId: sector.station.instanceId, slot: 'station' },
          });
          if (after !== next) { next = after; dispatched = true; break; }
        }
      }
      const readyDeployed = sector.deployedStack.find((instance) => {
        const def = defFor(instance.cardId);
        return def.deployed?.ability && isAbilityCharged(instance, 'deployed', def.deployed.ability, next.players.length);
      });
      if (readyDeployed) {
        const after = dispatch(next, {
          type: ACTION_TYPES.USE_ABILITY,
          payload: { playerId, instanceId: readyDeployed.instanceId, slot: 'deployed' },
        });
        if (after !== next) { next = after; dispatched = true; break; }
      }
    }
    if (!dispatched) break;
  }
  return next;
}

// The full turn for playerId, who must be the current active player -
// rolls, allocates its own dice, spends down to a card (or skips), uses
// whatever abilities come charged along the way, then ends the turn.
// Passive AI players' allocations are NOT handled here - store/index.js
// calls resolveAllocationForAI directly for those, independent of whose
// turn it nominally is (see that function's own comment).
export function playAITurn(state, playerId) {
  let next = state;
  const isMyTurn = () => next.players[next.meta.activePlayerIndex]?.id === playerId;
  let guard = 0;
  while (isMyTurn() && next.meta.turnPhase !== 'post-buy' && guard < 20) {
    guard += 1;
    next = tryUseReadyAbilities(next, playerId);
    next = resolvePendingSpends(next, playerId);
    if (!isMyTurn()) break;

    if (next.meta.turnPhase === 'pre-roll') {
      next = dispatch(next, { type: ACTION_TYPES.ROLL_DICE });
    } else if (next.meta.turnPhase === 'awaiting-allocation') {
      if (next.meta.dice.allocations[playerId]) break; // already allocated - waiting on other players, nothing left to do this pass
      next = resolveAllocationForAI(next, playerId);
    } else if (next.meta.turnPhase === 'post-reward') {
      const options = buyableOptions(next, playerId);
      next = options.length
        ? dispatch(next, {
          type: ACTION_TYPES.BUY_CARD,
          payload: {
            level: options[0].level,
            cardId: options[0].cardId,
            colonyCardId: options[0].colonyCardId,
            sectorOverride: options[0].colonyCardId ? undefined : sectorOverrideFor(defFor(options[0].cardId)),
            ...(options[0].colonyCardId ? colonyChoiceFor(next, playerId, defFor(options[0].colonyCardId)) : {}),
          },
        })
        : dispatch(next, { type: ACTION_TYPES.SKIP_BUY });
    } else {
      break;
    }
  }
  next = tryUseReadyAbilities(next, playerId);
  next = resolvePendingSpends(next, playerId);
  if (isMyTurn() && next.meta.turnPhase === 'post-buy' && !(next.meta.pendingClaims || []).some((c) => c.playerId === playerId) && !(next.meta.pendingExtraBuys || []).some((eb) => eb.playerId === playerId)) {
    next = dispatch(next, { type: ACTION_TYPES.END_TURN });
  }
  return next;
}
